import { useState } from "react";
import { useCreateSession } from "../../api/session/sessionQueries";
import { Button } from "../ui/button.tsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialogue.tsx";
import { Input } from "../ui/input.tsx";
import { Calendar } from "../ui/calendar.tsx";
import { toast } from "react-toastify";

export function AddSessionModal({ open, onOpenChange }) {
  const [formData, setFormData] = useState({
    title: "",
    startDate: null,
    endDate: null
  });

  const { mutate: createSession, isLoading } = useCreateSession();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    // Validate all required fields
    if (!formData.title?.trim()) {
      toast.error("Session name is required");
      return;
    }

    if (!formData.startDate) {
      toast.error("Start date is required");
      return;
    }

    if (!formData.endDate) {
      toast.error("End date is required");
      return;
    }

    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      toast.error("End date must be after start date");
      return;
    }

    // Create a new object with validated data
    const sessionData = {
      title: formData.title.trim(),
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate)
    };

    createSession(sessionData, {
      onSuccess: () => {
        onOpenChange(false);
        setFormData({
          title: "",
          startDate: null,
          endDate: null
        });
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">Add Academic Session</DialogTitle>
        </DialogHeader>

        <div className="py-6 space-y-6">
          <div>
            <label className="text-sm text-muted-foreground">Session Name</label>
            <Input
              placeholder="e.g. 2024/2025 Academic Year"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground">Start Date</label>
            <Calendar
              mode="single"
              selected={formData.startDate}
              onSelect={(date) => handleInputChange("startDate", date)}
              className="rounded-md border"
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground">End Date</label>
            <Calendar
              mode="single"
              selected={formData.endDate}
              onSelect={(date) => handleInputChange("endDate", date)}
              className="rounded-md border"
              disabled={(date) => 
                formData.startDate ? date < formData.startDate : false
              }
            />
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Creating..." : "Create Session"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}